import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, CalendarClock, Clock3, Link2, Percent, Plus, Trash2, Wallet } from 'lucide-react';
import { DebtEvent, DebtRecord, LoanType, Property, PropertyDebtLink } from '../types';
import { buildDebtLiveSnapshot, createDebtEvent, createDebtRecord, DebtScheduleRow, resolveDebtLinksForDebt } from '../services/debt';
import { formatCurrency0, formatNumber2, formatPercent2 } from '../utils/format';

interface DebtViewProps {
  debts: Record<string, DebtRecord>;
  debtLinks: PropertyDebtLink[];
  debtEvents: DebtEvent[];
  properties: Record<string, Property>;
  activePropertyId: string | null;
  requestedDebtId?: string | null;
  onSelectedDebtChange?: (debtId: string | null) => void;
  onSaveDebt: (debt: DebtRecord) => Promise<string | null> | string | null;
  onDeleteDebt: (id: string) => void;
  onSaveDebtEvent: (event: DebtEvent) => void;
  onDeleteDebtEvent: (id: string) => void;
  onUpsertDebtLink: (link: PropertyDebtLink) => Promise<string | null> | string | null;
  onRemoveDebtLink: (linkId: string) => Promise<void> | void;
  onAutoAllocateDebtByAcquisitionPrice: (debtId: string) => void;
}

const LOAN_TYPE_OPTIONS: Array<{ value: LoanType; label: string; help: string }> = [
  { value: 'amortizing', label: 'Amortizing', help: 'Fully amortizing debt with scheduled principal and interest.' },
  { value: 'interest_only', label: 'Interest-Only', help: 'Scheduled payments cover interest until maturity or payoff.' },
  { value: 'balloon', label: 'Balloon', help: 'Shorter-term debt with a balloon payoff date.' },
  { value: 'amortizing_balloon', label: 'Amortizing with Balloon', help: 'Scheduled amortization with a balloon maturity structure.' },
  { value: 'line_of_credit', label: 'Line of Credit', help: 'Balance-based carry without scheduled amortization.' },
  { value: 'custom', label: 'Custom', help: 'Flexible structure for non-standard debt terms.' }
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const parseIsoDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const toIsoDate = (value: Date) => value.toISOString().slice(0, 10);
const clampPaymentDay = (value?: string | number | null) => Math.max(1, Math.min(28, Number(value || 1)));
const addMonthsWithPaymentDay = (date: Date, months: number, paymentDay: number) => {
  const shiftedMonth = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDayOfMonth = new Date(shiftedMonth.getFullYear(), shiftedMonth.getMonth() + 1, 0).getDate();
  return new Date(shiftedMonth.getFullYear(), shiftedMonth.getMonth(), Math.min(paymentDay, lastDayOfMonth));
};
const deriveFirstPaymentDate = (originationDate: string, paymentDay: string | number) => {
  const parsedOriginationDate = parseIsoDate(originationDate);
  if (!parsedOriginationDate) return '';
  return toIsoDate(addMonthsWithPaymentDay(parsedOriginationDate, 1, clampPaymentDay(paymentDay)));
};
const getMonthGap = (startDate: string, endDate: string) => {
  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);
  if (!parsedStartDate || !parsedEndDate) return 0;
  return ((parsedEndDate.getFullYear() - parsedStartDate.getFullYear()) * 12)
    + (parsedEndDate.getMonth() - parsedStartDate.getMonth());
};
const DEBT_TIMELINE_MOUNT_DELAY_MS = 120;
const NUMERIC_DEBT_FIELDS = new Set([
  'originalBalance',
  'currentBalance',
  'interestRate',
  'amortYears',
  'interestOnlyMonths',
  'balloonAmount',
  'creditLimit',
  'paymentDayOfMonth'
]);
const STRING_DEBT_FIELDS = [
  'name',
  'lender',
  'notes',
  'originationDate',
  'firstPaymentDate',
  'maturityDate',
  'balloonDate'
] as const;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (value?: string | null) => Boolean(value && UUID_PATTERN.test(value));
type DebtLinkDraft = {
  id: string;
  debtId: string;
  propertyId: string;
  allocationPercent: number;
  allocationMethod: PropertyDebtLink['allocationMethod'];
  valueBasis?: PropertyDebtLink['valueBasis'];
  manualValueOverride?: number | null;
};
const resolvePropertySyncId = (propertyId: string | null | undefined, properties: Record<string, Property>) => {
  if (!propertyId) return null;
  const directProperty = properties[propertyId];
  if (directProperty) return directProperty.supabaseId ?? null;
  const matchedProperty = Object.values(properties).find((property) => property.supabaseId === propertyId) || null;
  return matchedProperty?.supabaseId ?? (isUuid(propertyId) ? propertyId : null);
};

const debtRecordToDraft = (debt: DebtRecord) => ({
  loanType: debt.loanType,
  name: debt.name || '',
  lender: debt.lender || '',
  ownershipEntity: debt.ownershipEntity || '',
  isSharedPortfolioDebt: Boolean(debt.isSharedPortfolioDebt),
  notes: debt.notes || '',
  originalBalance: String(debt.originalBalance || 0),
  currentBalance: String(debt.currentBalance || 0),
  interestRate: String(debt.interestRate || 0),
  amortYears: String(debt.amortYears || 0),
  originationDate: debt.originationDate || '',
  firstPaymentDate: debt.firstPaymentDate || '',
  paymentDayOfMonth: String(debt.paymentDayOfMonth || 1),
  maturityDate: debt.maturityDate || '',
  interestOnlyMonths: String(debt.interestOnlyMonths || 0),
  balloonDate: debt.balloonDate || '',
  balloonAmount: String(debt.balloonAmount || 0),
  creditLimit: String(debt.creditLimit || 0),
  assumeScheduledPaymentsMade: debt.assumeScheduledPaymentsMade
});

const buildDebtFromDraft = (
  debt: DebtRecord,
  draft: ReturnType<typeof debtRecordToDraft>
): DebtRecord => ({
  ...debt,
  loanType: draft.loanType,
  name: draft.name,
  lender: draft.lender,
  ownershipEntity: draft.ownershipEntity,
  isSharedPortfolioDebt: draft.isSharedPortfolioDebt,
  notes: draft.notes,
  originalBalance: Number(draft.originalBalance) || 0,
  currentBalance: Number(draft.currentBalance) || 0,
  interestRate: Number(draft.interestRate) || 0,
  amortYears: Number(draft.amortYears) || 0,
  originationDate: draft.originationDate,
  firstPaymentDate: draft.firstPaymentDate,
  paymentDayOfMonth: Number(draft.paymentDayOfMonth) || 0,
  maturityDate: draft.maturityDate,
  interestOnlyMonths: Number(draft.interestOnlyMonths) || 0,
  balloonDate: draft.balloonDate,
  balloonAmount: Number(draft.balloonAmount) || 0,
  creditLimit: Number(draft.creditLimit) || 0,
  assumeScheduledPaymentsMade: draft.assumeScheduledPaymentsMade,
  interestOnly: draft.loanType === 'interest_only' || draft.loanType === 'line_of_credit'
});

const debtDraftMatchesRecord = (debt: DebtRecord, draft: ReturnType<typeof debtRecordToDraft>) => {
  const currentDraft = debtRecordToDraft(debt);
  return JSON.stringify(currentDraft) === JSON.stringify(draft);
};

const DebtView = (props: DebtViewProps) => {
  const {
    debts,
    debtLinks,
    debtEvents,
    properties,
    activePropertyId,
    requestedDebtId,
    onSelectedDebtChange,
    onSaveDebt,
    onDeleteDebt,
    onSaveDebtEvent,
    onDeleteDebtEvent,
    onUpsertDebtLink,
    onRemoveDebtLink,
    onAutoAllocateDebtByAcquisitionPrice
  } = props;

  const [selectedDebtId, setSelectedDebtId] = useState(Object.keys(debts)[0] || null as string | null);
  const [selectedPropertyToLink, setSelectedPropertyToLink] = useState(activePropertyId || '');
  const [showExtraPaymentForm, setShowExtraPaymentForm] = useState(false);
  const [draftExtraPayment, setDraftExtraPayment] = useState({ date: todayIso(), amount: 0, notes: '' });
  const [draftDebt, setDraftDebt] = useState(null as ReturnType<typeof debtRecordToDraft> | null);
  const [draftDebtOwnerId, setDraftDebtOwnerId] = useState(null as string | null);
  const [pendingDebt, setPendingDebt] = useState(null as DebtRecord | null);
  const [isSavingPendingDebt, setIsSavingPendingDebt] = useState(false);
  const [isEditingDebt, setIsEditingDebt] = useState(false);
  const [isEditingLinks, setIsEditingLinks] = useState(false);
  const [linkDrafts, setLinkDrafts] = useState({} as Record<string, DebtLinkDraft>);
  const [linkDraftOwnerDebtId, setLinkDraftOwnerDebtId] = useState(null as string | null);
  const [showTimelineTable, setShowTimelineTable] = useState(false);
  const nameInputRef = useRef(null as HTMLInputElement | null);
  const focusedDraftIdRef = useRef(null as string | null);
  const firstPaymentManuallyEditedRef = useRef(false);
  const lastRequestedDebtIdRef = useRef(requestedDebtId ?? null as string | null);
  const lastHydratedDebtKeyRef = useRef(null as string | null);
  const lastSyncedActivePropertyIdRef = useRef(activePropertyId || '');
  const propertyPickerTouchedRef = useRef(false);
  const [detailsDismissed, setDetailsDismissed] = useState(false);

  const visibleDebtList = useMemo(() => {
    const propertyLookup = new Map<string, Property>();
    Object.values(properties).forEach((property) => {
      propertyLookup.set(property.id, property);
      if (property.supabaseId) {
        propertyLookup.set(property.supabaseId, property);
      }
    });

    const deduped = new Map<string, DebtRecord>();
    (Object.values(debts) as DebtRecord[]).forEach((debt) => {
      const resolvedProperty = debt.propertyId ? propertyLookup.get(debt.propertyId) || null : null;
      if (!resolvedProperty) return;

      // Match the sidebar's canonical debt list so the left rail, center list,
      // and details pane all speak the same debt identity and ordering.
      const canonicalKey = debt.supabaseId
        || `${resolvedProperty.id}:${(debt.name || '').trim().toLowerCase()}:${(debt.lender || '').trim().toLowerCase()}`;
      const existing = deduped.get(canonicalKey);
      const debtTimestamp = debt.updatedAt || debt.createdAt || 0;
      const existingTimestamp = existing ? (existing.updatedAt || existing.createdAt || 0) : 0;

      if (!existing || debtTimestamp >= existingTimestamp) {
        deduped.set(canonicalKey, debt);
      }
    });

    return [...deduped.values()].sort((left: DebtRecord, right: DebtRecord) => (
      (right.updatedAt || right.createdAt || 0) - (left.updatedAt || left.createdAt || 0)
    ));
  }, [debts, properties]);

  useEffect(() => {
    if (requestedDebtId === lastRequestedDebtIdRef.current) return;
    lastRequestedDebtIdRef.current = requestedDebtId ?? null;
    if (!requestedDebtId) return;
    if (requestedDebtId === selectedDebtId) return;
    if (!debts[requestedDebtId] && pendingDebt?.id !== requestedDebtId) return;
    setSelectedDebtId(requestedDebtId);
    setDetailsDismissed(false);
  }, [debts, pendingDebt, requestedDebtId, selectedDebtId]);

  useEffect(() => {
    onSelectedDebtChange?.(selectedDebtId);
  }, [onSelectedDebtChange, selectedDebtId]);

  useEffect(() => {
    const debtIds = visibleDebtList.map((debt: DebtRecord) => debt.id);
    if (isEditingDebt || isEditingLinks) return;
    if (!selectedDebtId && debtIds.length > 0) {
      if (detailsDismissed && !pendingDebt) return;
      setSelectedDebtId(debtIds[0]);
      return;
    }

    if (selectedDebtId && pendingDebt && selectedDebtId === pendingDebt.id) {
      return;
    }

    if (selectedDebtId && debtIds.length > 0 && !debts[selectedDebtId]) {
      setSelectedDebtId(debtIds[0]);
      return;
    }

    if (debtIds.length === 0 && !pendingDebt) {
      setSelectedDebtId(null);
    }
  }, [detailsDismissed, isEditingDebt, isEditingLinks, pendingDebt, selectedDebtId, visibleDebtList]);

  useEffect(() => {
    // Only prefill the link picker when the external property selection actually changes.
    // Without this guard, every upstream activePropertyId refresh can overwrite the user's
    // in-progress picker choice and make the select appear to "jump" between properties.
    const nextActivePropertyId = activePropertyId || '';
    if (nextActivePropertyId === lastSyncedActivePropertyIdRef.current) return;
    lastSyncedActivePropertyIdRef.current = nextActivePropertyId;
    if (!nextActivePropertyId || propertyPickerTouchedRef.current) return;
    setSelectedPropertyToLink(nextActivePropertyId);
  }, [activePropertyId]);

  useEffect(() => {
    if (!pendingDebt) return;
    setDetailsDismissed(false);
    if (selectedDebtId === pendingDebt.id) return;
    setSelectedDebtId(pendingDebt.id);
  }, [pendingDebt, selectedDebtId]);

  const debtSnapshots = useMemo(() => (
    visibleDebtList.reduce((acc: Record<string, ReturnType<typeof buildDebtLiveSnapshot>>, debt: DebtRecord) => {
      acc[debt.id] = buildDebtLiveSnapshot(debt, debtEvents.filter((event) => event.debtId === debt.id), undefined, { includeScheduleRows: false });
      return acc;
    }, {} as Record<string, ReturnType<typeof buildDebtLiveSnapshot>>)
  ), [debtEvents, visibleDebtList]);
  const pendingDebtSnapshot = useMemo(() => (
    pendingDebt ? buildDebtLiveSnapshot(pendingDebt, [], undefined, { includeScheduleRows: false }) : null
  ), [pendingDebt]);
  const debtSnapshotList = useMemo(() => {
    const snapshots = Object.values(debtSnapshots) as ReturnType<typeof buildDebtLiveSnapshot>[];
    if (!pendingDebtSnapshot) return snapshots;
    if (snapshots.some((snapshot) => snapshot.debt.id === pendingDebtSnapshot.debt.id)) return snapshots;
    return [pendingDebtSnapshot, ...snapshots];
  }, [debtSnapshots, pendingDebtSnapshot]);

  const selectedDebtSnapshot = useMemo(() => (
    selectedDebtId && debts[selectedDebtId]
      ? buildDebtLiveSnapshot(debts[selectedDebtId], debtEvents.filter((event) => event.debtId === selectedDebtId))
      : null
  ), [debtEvents, debts, selectedDebtId]);
  const selectedDebt = selectedDebtSnapshot?.debt || (pendingDebt && pendingDebt.id === selectedDebtId ? pendingDebt : null);
  const selectedDebtDraft = selectedDebt
    ? (draftDebt && draftDebtOwnerId === selectedDebt.id ? draftDebt : debtRecordToDraft(selectedDebt))
    : null;
  const displaySelectedDebtSnapshot = selectedDebtSnapshot || (pendingDebt && pendingDebt.id === selectedDebtId ? pendingDebtSnapshot : null);
  const selectedDebtSyncId = selectedDebt?.supabaseId || (selectedDebt && isUuid(selectedDebt.id) ? selectedDebt.id : null);
  const selectedDebtEvents = useMemo(() => (
    selectedDebtSyncId ? debtEvents.filter((event) => event.debtId === selectedDebtSyncId).sort((left, right) => right.date.localeCompare(left.date)) : []
  ), [debtEvents, selectedDebtSyncId]);

  const linksForSelectedDebt = useMemo(() => (
    selectedDebtSyncId ? debtLinks.filter((link) => link.debtId === selectedDebtSyncId) : []
  ), [debtLinks, selectedDebtSyncId]);

  const selectedPropertyToLinkSyncId = useMemo(() => (
    resolvePropertySyncId(selectedPropertyToLink, properties)
  ), [properties, selectedPropertyToLink]);

  const draftAwareLinksForSelectedDebt = useMemo(() => {
    if (!selectedDebtSyncId) return debtLinks;
    if (!isEditingLinks || linkDraftOwnerDebtId !== selectedDebtSyncId) return debtLinks;

    const drafts = Object.values(linkDrafts) as DebtLinkDraft[];
    const draftIds = new Set(drafts.map((draft) => draft.id));
    const persistedForDebt = debtLinks.filter((link) => link.debtId === selectedDebtSyncId && !draftIds.has(link.id));

    return [
      ...debtLinks.filter((link) => link.debtId !== selectedDebtSyncId),
      ...persistedForDebt,
      ...drafts
    ];
  }, [debtLinks, isEditingLinks, linkDraftOwnerDebtId, linkDrafts, selectedDebtSyncId]);

  const resolvedLinksForSelectedDebt = useMemo(() => (
    selectedDebtSyncId ? resolveDebtLinksForDebt(selectedDebtSyncId, draftAwareLinksForSelectedDebt, properties, debts, debtEvents) : []
  ), [selectedDebtSyncId, draftAwareLinksForSelectedDebt, properties, debts, debtEvents]);

  const selectedDebtEntity = useMemo(() => (
    (selectedDebtDraft?.ownershipEntity || selectedDebt?.ownershipEntity || '').trim()
  ), [selectedDebt?.ownershipEntity, selectedDebtDraft?.ownershipEntity]);
  const ownershipEntityOptions = useMemo(() => {
    const values = new Set<string>();
    Object.values(properties).forEach((property: Property) => {
      const entity = (property.ownershipEntity || '').trim();
      if (entity) values.add(entity);
    });
    (Object.values(debts) as DebtRecord[]).forEach((debt: DebtRecord) => {
      const entity = (debt.ownershipEntity || '').trim();
      if (entity) values.add(entity);
    });
    if (selectedDebtEntity) values.add(selectedDebtEntity);
    return [...values].sort((left, right) => left.localeCompare(right));
  }, [debts, properties, selectedDebtEntity]);
  const isCustomDebtEntity = Boolean(selectedDebtEntity && !ownershipEntityOptions.includes(selectedDebtEntity));

  const allocationTotal = useMemo(() => (
    resolvedLinksForSelectedDebt.reduce((sum: number, entry: any) => sum + entry.allocationPercent, 0)
  ), [resolvedLinksForSelectedDebt]);

  const availablePropertiesToLink = useMemo(() => (
    Object.values(properties).filter((property: Property) => {
      if (selectedDebtDraft?.isSharedPortfolioDebt && selectedDebtEntity) {
        const propertyEntity = (property.ownershipEntity || '').trim();
        if (propertyEntity.toLowerCase() !== selectedDebtEntity.toLowerCase()) {
          return false;
        }
      }
      const propertySyncId = property.supabaseId ?? null;
      return !linksForSelectedDebt.some((link: PropertyDebtLink) => (
        link.propertyId === property.id ||
        (propertySyncId && link.propertyId === propertySyncId)
      ));
    })
  ), [linksForSelectedDebt, properties, selectedDebtDraft?.isSharedPortfolioDebt, selectedDebtEntity]);

  const eligibleEntityProperties = useMemo(() => {
    if (!(selectedDebtDraft?.isSharedPortfolioDebt && selectedDebtEntity)) return [] as Property[];
    return Object.values(properties).filter((property: Property) => (
      ((property.ownershipEntity || '').trim().toLowerCase() === selectedDebtEntity.toLowerCase())
    ));
  }, [properties, selectedDebtDraft?.isSharedPortfolioDebt, selectedDebtEntity]);

  const linkedEligiblePropertyCount = useMemo(() => {
    const eligibleIds = new Set(eligibleEntityProperties.flatMap((property: Property) => [property.id, property.supabaseId].filter(Boolean) as string[]));
    return linksForSelectedDebt.filter((link: PropertyDebtLink) => eligibleIds.has(link.propertyId)).length;
  }, [eligibleEntityProperties, linksForSelectedDebt]);

  const normalizedLtvState = useMemo(() => {
    const normalizedEntries = resolvedLinksForSelectedDebt.filter((entry: any) => entry.link.allocationMethod === 'normalize_ltv');
    if (normalizedEntries.length === 0) {
      return { impliedLtv: null as number | null, totalValue: 0, isInvalid: false };
    }
    const totalValue = normalizedEntries.reduce((sum: number, entry: any) => sum + (entry.allocationValue || 0), 0);
    return {
      impliedLtv: normalizedEntries[0]?.portfolioLtv ?? null,
      totalValue,
      isInvalid: totalValue <= 0
    };
  }, [resolvedLinksForSelectedDebt]);

  const sharedDebtAllocationMethod = useMemo(() => {
    if (linksForSelectedDebt.length === 0) return 'manual';
    const methods = new Set((Object.values(linkDrafts).length > 0 ? Object.values(linkDrafts) : linksForSelectedDebt).map((link: any) => link.allocationMethod));
    return methods.size === 1 ? Array.from(methods)[0] as PropertyDebtLink['allocationMethod'] : 'manual';
  }, [linkDrafts, linksForSelectedDebt]);

  const sharedDebtValueBasis = useMemo(() => {
    if (linksForSelectedDebt.length === 0) return 'purchase_price';
    const source = Object.values(linkDrafts).length > 0 ? Object.values(linkDrafts) : linksForSelectedDebt;
    const values = new Set(source.map((link: any) => link.valueBasis || 'purchase_price'));
    return values.size === 1 ? Array.from(values)[0] as PropertyDebtLink['valueBasis'] : 'purchase_price';
  }, [linkDrafts, linksForSelectedDebt]);

  const validationState = useMemo(() => {
    if (selectedDebtDraft?.isSharedPortfolioDebt && !selectedDebtEntity) {
      return {
        label: 'Entity Required',
        detail: 'Shared portfolio debt must be assigned to an ownership entity / LLC before allocation.',
        className: 'text-amber-700 border-amber-200 bg-amber-50'
      };
    }
    if (selectedDebtDraft?.isSharedPortfolioDebt && selectedDebtEntity && eligibleEntityProperties.length === 0) {
      return {
        label: 'No Eligible Entity Properties',
        detail: `No saved properties currently use ${selectedDebtEntity}. Set that ownership entity on the properties first, then come back to link them.`,
        className: 'text-amber-700 border-amber-200 bg-amber-50'
      };
    }
    if (resolvedLinksForSelectedDebt.length === 0) {
      return {
        label: 'No linked properties',
        detail: selectedDebtDraft?.isSharedPortfolioDebt && selectedDebtEntity
          ? `Choose one of the eligible ${selectedDebtEntity} properties and click Add Link.`
          : 'This debt is not assigned to any property yet.',
        className: 'text-amber-700 border-amber-200 bg-amber-50'
      };
    }
    if (normalizedLtvState.isInvalid) {
      return {
        label: 'Value Basis Needed',
        detail: 'Normalize by Portfolio LTV needs property values above zero. Check purchase price, current value, or manual overrides.',
        className: 'text-rose-700 border-rose-200 bg-rose-50'
      };
    }
    if (allocationTotal > 100.01) {
      return {
        label: 'Over-allocated',
        detail: 'Allocated percentages exceed 100% and will overstate debt service.',
        className: 'text-rose-700 border-rose-200 bg-rose-50'
      };
    }
    if (allocationTotal < 99.99) {
      return {
        label: 'Incomplete',
        detail: 'Allocated percentages are below 100%, so not all debt is assigned.',
        className: 'text-amber-700 border-amber-200 bg-amber-50'
      };
    }
    return {
      label: 'Valid',
      detail: 'Allocation totals 100% across the linked property rows.',
      className: 'text-emerald-700 border-emerald-200 bg-emerald-50'
    };
  }, [allocationTotal, eligibleEntityProperties.length, normalizedLtvState.isInvalid, resolvedLinksForSelectedDebt.length, selectedDebtDraft?.isSharedPortfolioDebt, selectedDebtEntity]);

  const hasPersistedDebtRecords = Object.keys(debts).length > 0;
  const shouldShowEmptyState = !hasPersistedDebtRecords && !selectedDebtId;
  const isSelectedDebtSynced = Boolean(selectedDebt?.supabaseId && isUuid(selectedDebt.supabaseId));
  const isSelectedDebtPending = Boolean(selectedDebt && !isSelectedDebtSynced);
  const debtDetailsEditable = isSelectedDebtPending || isEditingDebt;
  const selectedLoanType = (selectedDebtDraft && (isEditingDebt || isSelectedDebtPending)
    ? selectedDebtDraft.loanType as LoanType | undefined
    : undefined) || selectedDebt?.loanType || 'amortizing';
  const selectedLoanTypeMeta = LOAN_TYPE_OPTIONS.find((option) => option.value === selectedLoanType) || LOAN_TYPE_OPTIONS[0];
  const canEditSelectedDebt = Boolean(selectedDebt && !isSelectedDebtPending);
  const selectedPropertyIsSynced = Boolean(selectedPropertyToLinkSyncId && isUuid(selectedPropertyToLinkSyncId));
  const hasOpenDraftDebt = Boolean(pendingDebt);
  const hasUnsavedSelectedDebtChanges = Boolean(selectedDebt && selectedDebtDraft && !debtDraftMatchesRecord(selectedDebt, selectedDebtDraft));
  const firstPaymentGapMonths = useMemo(() => (
    selectedDebtDraft ? getMonthGap(selectedDebtDraft.originationDate, selectedDebtDraft.firstPaymentDate) : 0
  ), [selectedDebtDraft]);

  const commitSelectedDebt = useCallback((nextDraft?: ReturnType<typeof debtRecordToDraft>) => {
    if (!selectedDebt) return;
    const draftToCommit = nextDraft || selectedDebtDraft || debtRecordToDraft(selectedDebt);
    if (debtDraftMatchesRecord(selectedDebt, draftToCommit)) return;
    const committedDebt = buildDebtFromDraft(selectedDebt, draftToCommit);
    if (isSelectedDebtPending) {
      setPendingDebt(committedDebt);
      return;
    }
    void onSaveDebt(committedDebt);
  }, [isSelectedDebtPending, onSaveDebt, selectedDebt, selectedDebtDraft]);

  useEffect(() => {
    if (!selectedDebt) {
      setDraftDebt(null);
      setDraftDebtOwnerId(null);
      setIsEditingDebt(false);
      setIsEditingLinks(false);
      setLinkDrafts({});
      setLinkDraftOwnerDebtId(null);
      propertyPickerTouchedRef.current = false;
      lastHydratedDebtKeyRef.current = null;
      return;
    }
    // Hydrate local edit state only when the selected debt identity changes.
    // Depending on the whole selectedDebt object here causes local draft state to be rebuilt
    // whenever upstream debt snapshots are recalculated, which makes the UI flicker.
    const hydrationKey = `${selectedDebt.id}:${isSelectedDebtSynced ? 'synced' : 'pending'}`;
    if (lastHydratedDebtKeyRef.current === hydrationKey) return;
    lastHydratedDebtKeyRef.current = hydrationKey;
    firstPaymentManuallyEditedRef.current = false;
    setDraftDebt(debtRecordToDraft(selectedDebt));
    setDraftDebtOwnerId(selectedDebt.id);
    setIsEditingDebt(!isSelectedDebtSynced);
    propertyPickerTouchedRef.current = false;
    lastSyncedActivePropertyIdRef.current = activePropertyId || '';
    setSelectedPropertyToLink(activePropertyId || '');
  }, [activePropertyId, isSelectedDebtSynced, selectedDebt?.id]);

  useEffect(() => {
    if (!selectedDebtSyncId) {
      setIsEditingLinks(false);
      setLinkDrafts({});
      setLinkDraftOwnerDebtId(null);
      return;
    }

    if (isEditingLinks && linkDraftOwnerDebtId === selectedDebtSyncId) return;
    setIsEditingLinks(false);
    setLinkDrafts({});
    setLinkDraftOwnerDebtId(null);
  }, [isEditingLinks, linkDraftOwnerDebtId, selectedDebtSyncId]);

  useEffect(() => {
    if (!selectedDebt || !pendingDebt || selectedDebt.id !== pendingDebt.id) return;
    if (focusedDraftIdRef.current === pendingDebt.id) return;
    focusedDraftIdRef.current = pendingDebt.id;
    const timeoutId = window.setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [pendingDebt, selectedDebt]);

  useEffect(() => {
    if (!pendingDebt) return;
    if (debts[pendingDebt.id]) {
      setPendingDebt(null);
    }
  }, [debts, pendingDebt]);

  useEffect(() => {
    setShowTimelineTable(false);
    if (!selectedDebtId) return;
    const timeoutId = window.setTimeout(() => {
      setShowTimelineTable(true);
    }, DEBT_TIMELINE_MOUNT_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [selectedDebtId]);

  useEffect(() => {
    if (selectedPropertyToLink) return;
    if (propertyPickerTouchedRef.current) return;
    if (availablePropertiesToLink.length === 1) {
      setSelectedPropertyToLink(availablePropertiesToLink[0].id);
    }
  }, [availablePropertiesToLink, selectedPropertyToLink]);

  const updateDraftField = (field: keyof ReturnType<typeof debtRecordToDraft>, value: string | boolean) => {
    if (field === 'firstPaymentDate') {
      firstPaymentManuallyEditedRef.current = Boolean(String(value || '').trim());
    }
    if (selectedDebt) {
      setDraftDebtOwnerId(selectedDebt.id);
    }
    setDraftDebt((current: ReturnType<typeof debtRecordToDraft> | null) => {
      const base = current || (selectedDebt ? debtRecordToDraft(selectedDebt) : null);
      if (!base) return current;
      const nextDraft = {
        ...base,
        [field]: value
      };
      if ((field === 'originationDate' || field === 'paymentDayOfMonth') && !firstPaymentManuallyEditedRef.current) {
        const nextFirstPaymentDate = deriveFirstPaymentDate(
          field === 'originationDate' ? String(value) : nextDraft.originationDate,
          field === 'paymentDayOfMonth' ? String(value) : nextDraft.paymentDayOfMonth
        );
        if (nextFirstPaymentDate) {
          nextDraft.firstPaymentDate = nextFirstPaymentDate;
        }
      }
      return nextDraft;
    });
  };

  const addDebt = () => {
    if (pendingDebt) {
      setSelectedDebtId(pendingDebt.id);
      return;
    }
    const debt = {
      ...createDebtRecord(),
      propertyId: activePropertyId ?? null
    };
    setPendingDebt(debt);
    setDraftDebt(debtRecordToDraft(debt));
    setDraftDebtOwnerId(debt.id);
    setIsEditingDebt(true);
    setShowExtraPaymentForm(false);
    setShowTimelineTable(false);
    setDetailsDismissed(false);
    setSelectedDebtId(debt.id);
  };

  const savePendingDebt = async (options?: { closeAfterSave?: boolean }) => {
    if (!pendingDebt || !selectedDebtDraft) return;
    const debtToPersist = buildDebtFromDraft(pendingDebt, selectedDebtDraft);
    setPendingDebt(debtToPersist);
    setIsSavingPendingDebt(true);
    try {
      const savedDebtId = await onSaveDebt(debtToPersist);
      if (savedDebtId) {
        setPendingDebt(null);
        setDraftDebt(null);
        setDraftDebtOwnerId(null);
        setIsEditingDebt(false);
        setDetailsDismissed(Boolean(options?.closeAfterSave));
        setSelectedDebtId(options?.closeAfterSave ? null : savedDebtId);
      }
    } finally {
      setIsSavingPendingDebt(false);
    }
  };

  const discardPendingDebt = useCallback(() => {
    if (!pendingDebt) return;
    setPendingDebt(null);
    setDraftDebt(null);
    setDraftDebtOwnerId(null);
    setIsEditingDebt(false);
    setDetailsDismissed(false);
    setSelectedDebtId(Object.keys(debts)[0] || null);
  }, [debts, pendingDebt]);

  const beginEditingSelectedDebt = useCallback(() => {
    if (!selectedDebt) return;
    firstPaymentManuallyEditedRef.current = false;
    setDraftDebt(debtRecordToDraft(selectedDebt));
    setDraftDebtOwnerId(selectedDebt.id);
    setIsEditingDebt(true);
  }, [selectedDebt]);

  const cancelEditingSelectedDebt = useCallback(() => {
    if (!selectedDebt) return;
    firstPaymentManuallyEditedRef.current = false;
    setDraftDebt(debtRecordToDraft(selectedDebt));
    setDraftDebtOwnerId(selectedDebt.id);
    setIsEditingDebt(false);
  }, [selectedDebt]);

  const saveSelectedDebtChanges = useCallback(() => {
    if (!selectedDebt || !selectedDebtDraft) return;
    commitSelectedDebt(selectedDebtDraft);
    if (!isSelectedDebtPending) {
      setIsEditingDebt(false);
    }
  }, [commitSelectedDebt, isSelectedDebtPending, selectedDebt, selectedDebtDraft]);

  const beginEditingLinks = useCallback(() => {
    if (!selectedDebtSyncId) return;
    const nextDrafts = Object.fromEntries(
      linksForSelectedDebt.map((link: PropertyDebtLink) => [link.id, {
        id: link.id,
        debtId: link.debtId,
        propertyId: link.propertyId,
        allocationPercent: link.allocationPercent,
        allocationMethod: link.allocationMethod,
        valueBasis: link.valueBasis || 'purchase_price',
        manualValueOverride: link.manualValueOverride ?? null
      }])
    ) as Record<string, DebtLinkDraft>;
    setLinkDrafts(nextDrafts);
    setLinkDraftOwnerDebtId(selectedDebtSyncId);
    setIsEditingLinks(true);
  }, [linksForSelectedDebt, selectedDebtSyncId]);

  const cancelEditingLinks = useCallback(() => {
    setIsEditingLinks(false);
    setLinkDrafts({});
    setLinkDraftOwnerDebtId(null);
  }, []);

  const updateLinkDraft = useCallback((linkId: string, updates: Partial<DebtLinkDraft>) => {
    setLinkDrafts((current: Record<string, DebtLinkDraft>) => {
      const existing = current[linkId];
      if (!existing) return current;
      return {
        ...current,
        [linkId]: {
          ...existing,
          ...updates
        }
      };
    });
  }, []);

  const applyAllocationMethodToAllLinks = useCallback((allocationMethod: PropertyDebtLink['allocationMethod']) => {
    if (!isEditingLinks) return;
    setLinkDrafts((current: Record<string, DebtLinkDraft>) => (
      Object.fromEntries(
        Object.entries(current).map(([id, draft]) => [id, {
          ...draft,
          allocationMethod
        }])
      )
    ));
  }, [isEditingLinks]);

  const applyValueBasisToAllLinks = useCallback((valueBasis: PropertyDebtLink['valueBasis']) => {
    if (!isEditingLinks) return;
    setLinkDrafts((current: Record<string, DebtLinkDraft>) => (
      Object.fromEntries(
        Object.entries(current).map(([id, draft]) => [id, {
          ...draft,
          valueBasis
        }])
      )
    ));
  }, [isEditingLinks]);

  const saveLinkDrafts = useCallback(async () => {
    const drafts = Object.values(linkDrafts) as DebtLinkDraft[];
    for (const draft of drafts) {
      await onUpsertDebtLink(draft);
    }
    setIsEditingLinks(false);
    setLinkDrafts({});
    setLinkDraftOwnerDebtId(null);
  }, [linkDrafts, onUpsertDebtLink]);

  const fieldGroups = useMemo(() => {
    switch (selectedLoanType) {
      case 'interest_only':
        return ['originalBalance', 'originationDate', 'firstPaymentDate', 'paymentDayOfMonth', 'interestRate', 'interestOnlyMonths', 'assumeScheduledPaymentsMade'];
      case 'amortizing_balloon':
        return ['originalBalance', 'originationDate', 'firstPaymentDate', 'paymentDayOfMonth', 'interestRate', 'interestOnlyMonths', 'amortYears', 'balloonDate', 'assumeScheduledPaymentsMade'];
      case 'balloon':
        return ['originalBalance', 'originationDate', 'firstPaymentDate', 'paymentDayOfMonth', 'interestRate', 'amortYears', 'balloonDate', 'balloonAmount', 'assumeScheduledPaymentsMade'];
      case 'line_of_credit':
        return ['currentBalance', 'originationDate', 'firstPaymentDate', 'paymentDayOfMonth', 'interestRate', 'creditLimit'];
      case 'custom':
        return ['originalBalance', 'originationDate', 'firstPaymentDate', 'paymentDayOfMonth', 'interestRate', 'amortYears', 'interestOnlyMonths', 'assumeScheduledPaymentsMade'];
      default:
        return ['originalBalance', 'originationDate', 'firstPaymentDate', 'paymentDayOfMonth', 'interestRate', 'amortYears', 'assumeScheduledPaymentsMade'];
    }
  }, [selectedLoanType]);

  const renderField = (field: string) => {
    if (!selectedDebt || !selectedDebtDraft) return null;
    const editable = debtDetailsEditable;
    const commonInputClassName = `w-full rounded-2xl border border-slate-700 px-4 py-3 text-sm font-medium text-white outline-none ${editable ? 'bg-slate-950 focus:border-emerald-500' : 'cursor-default border-slate-800 bg-slate-950/60 text-slate-300 opacity-85'}`;
    const labels: Record<string, string> = {
      originalBalance: 'Original Balance',
      currentBalance: 'Starting Current Balance',
      interestRate: 'Interest Rate',
      amortYears: 'Amort Years',
      originationDate: 'Origination Date',
      firstPaymentDate: 'First Payment Date',
      paymentDayOfMonth: 'Payment Day',
      interestOnlyMonths: 'Interest-Only Months',
      balloonDate: 'Balloon Date',
      balloonAmount: 'Balloon Amount',
      creditLimit: 'Credit Limit',
      assumeScheduledPaymentsMade: 'Assume Scheduled Payments Made'
    };

    if (field === 'assumeScheduledPaymentsMade') {
      return (
        <label key={field} className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-4">
          <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{labels[field]}</span>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-slate-300">Advance the balance automatically as scheduled payment dates pass.</p>
            <input
              type="checkbox"
              checked={selectedDebtDraft.assumeScheduledPaymentsMade}
              disabled={!editable}
              onChange={(e: any) => {
                if (!editable) return;
                updateDraftField('assumeScheduledPaymentsMade', e.target.checked);
              }}
              className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500/20"
            />
          </div>
        </label>
      );
    }

    const isNumeric = NUMERIC_DEBT_FIELDS.has(field);
    const isDate = field.toLowerCase().includes('date');
    const readOnly = !editable || (field === 'currentBalance' && selectedLoanType !== 'line_of_credit');

    return (
      <div key={field}>
        <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{labels[field]}</label>
        <input
          type={isNumeric ? 'number' : isDate ? 'date' : 'text'}
          value={(selectedDebtDraft as any)[field] ?? (isNumeric ? '0' : '')}
          readOnly={readOnly}
          onChange={(e: any) => updateDraftField(field as keyof ReturnType<typeof debtRecordToDraft>, e.target.value)}
          tabIndex={editable ? 0 : -1}
          className={`${commonInputClassName} ${readOnly ? 'cursor-not-allowed' : ''}`}
        />
        {field === 'currentBalance' && readOnly && <p className="mt-2 text-xs text-slate-500">This balance is derived from loan timing and extra principal events.</p>}
        {field === 'originationDate' && (
          <p className="mt-2 text-xs text-slate-500">Origination starts the loan, but scheduled amortization begins on the first payment date.</p>
        )}
        {field === 'firstPaymentDate' && firstPaymentGapMonths > 2 && (
          <p className="mt-2 text-xs text-amber-300">
            Scheduled amortization will not begin until this date. It is about {firstPaymentGapMonths} months after origination, so earlier paydown is currently excluded.
          </p>
        )}
      </div>
    );
  };

  if (shouldShowEmptyState) {
    return (
      <div className="space-y-6">
        <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-8 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Financing</p>
          <h2 className="mt-3 text-2xl font-black text-white">Create debt separately from the property</h2>
          <p className="mt-3 text-sm text-slate-400">Model one loan across multiple properties, then let the balance amortize over time like a live debt instrument.</p>
          <button onClick={addDebt} className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100">
            <Plus size={14} /> Create First Debt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col xl:flex-row gap-6">
        <div className="xl:w-80 rounded-[2rem] border border-slate-700/60 bg-slate-900 p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Debt Records</p>
            <button
              onClick={addDebt}
              disabled={hasOpenDraftDebt}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-2 text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
            >
              <Plus size={14} />
            </button>
          </div>
          {hasOpenDraftDebt ? (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] text-amber-700">
              Finish saving or discard the draft debt before switching to another loan.
            </div>
          ) : null}
          <div className="space-y-2">
            {debtSnapshotList.map((snapshot: ReturnType<typeof buildDebtLiveSnapshot>) => (
              <button
                key={snapshot.debt.id}
                onClick={() => {
                  if (hasOpenDraftDebt && snapshot.debt.id !== pendingDebt?.id) return;
                  setSelectedDebtId(snapshot.debt.id);
                }}
                disabled={hasOpenDraftDebt && snapshot.debt.id !== pendingDebt?.id}
                className={`w-full rounded-2xl border px-4 py-3 text-left transition-all disabled:cursor-not-allowed ${
                  selectedDebtId === snapshot.debt.id
                    ? 'border-[color:var(--line-strong)] bg-[color:var(--surface-soft)] shadow-sm'
                    : hasOpenDraftDebt && snapshot.debt.id !== pendingDebt?.id
                      ? 'border-slate-200 bg-slate-50 opacity-45'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <p className="text-sm font-black text-slate-900">{snapshot.debt.name}</p>
                <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{snapshot.debt.lender || 'No lender set'}</p>
                <p className="mt-2 text-[11px] text-slate-700">{formatCurrency0(snapshot.currentBalance)} live balance</p>
                <p className="mt-1 text-[10px] text-slate-500">{snapshot.nextPaymentDate ? `Next ${snapshot.nextPaymentDate}` : 'No future payment scheduled'}</p>
              </button>
            ))}
          </div>
        </div>

        {!selectedDebt && !hasOpenDraftDebt ? (
          <div className="flex-1 rounded-[2rem] border border-slate-700/60 bg-slate-900 p-8 text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Debt Details</p>
            <h2 className="mt-3 text-2xl font-black text-white">Select a debt to edit</h2>
            <p className="mt-3 text-sm text-slate-400">Choose an existing loan from the left, or start a new one with the plus button.</p>
          </div>
        ) : null}

        {selectedDebt && displaySelectedDebtSnapshot && (
          <div className="flex-1 space-y-6">
            <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Debt Details</p>
                  <h2 className="mt-2 text-2xl font-black text-white">{selectedDebt.name}</h2>
                  <p className="mt-2 text-sm text-slate-500">{selectedLoanTypeMeta.help}</p>
                  {isSelectedDebtPending ? (
                    <p className="mt-2 text-[10px] font-black uppercase tracking-[0.18em] text-amber-300">Draft debt not saved yet</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-3">
                  {isSelectedDebtPending ? (
                    <>
                      <button
                        onClick={() => savePendingDebt()}
                        disabled={isSavingPendingDebt}
                        className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                      >
                        {isSavingPendingDebt ? 'Saving...' : 'Save Debt'}
                      </button>
                      <button
                        onClick={() => savePendingDebt({ closeAfterSave: true })}
                        disabled={isSavingPendingDebt}
                        className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-700 shadow-sm transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                      >
                        {isSavingPendingDebt ? 'Saving...' : 'Save & Close'}
                      </button>
                    </>
                  ) : (
                    <>
                      {isEditingDebt ? (
                        <>
                          <button
                            onClick={saveSelectedDebtChanges}
                            disabled={!hasUnsavedSelectedDebtChanges}
                            className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                          >
                            Save Changes
                          </button>
                          <button
                            onClick={cancelEditingSelectedDebt}
                            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={beginEditingSelectedDebt}
                          disabled={!canEditSelectedDebt}
                          className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                        >
                          Update Debt
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setDetailsDismissed(true);
                          setSelectedDebtId(null);
                          setIsEditingDebt(false);
                        }}
                        className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
                      >
                        Close
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      if (isSelectedDebtPending) {
                        discardPendingDebt();
                        return;
                      }
                      onDeleteDebt(selectedDebt.id);
                      setSelectedDebtId(Object.keys(debts).filter((id) => id !== selectedDebt.id)[0] || null);
                    }}
                    className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-600 shadow-sm transition-colors hover:bg-rose-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Name</label>
                  <input
                    ref={nameInputRef}
                    value={selectedDebtDraft?.name || ''}
                    onChange={(e: any) => updateDraftField('name', e.target.value)}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium outline-none ${debtDetailsEditable ? 'border-slate-700 bg-slate-950 text-white focus:border-emerald-500' : 'cursor-default border-slate-800 bg-slate-950/60 text-slate-300'}`}
                    readOnly={!debtDetailsEditable}
                    tabIndex={debtDetailsEditable ? 0 : -1}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Loan Type</label>
                  <select
                    value={selectedLoanType}
                    onChange={(e: any) => {
                      if (!debtDetailsEditable) return;
                      const nextLoanType = e.target.value as LoanType;
                      const nextDraft = {
                        ...(selectedDebtDraft || debtRecordToDraft(selectedDebt)),
                        loanType: nextLoanType,
                        amortYears: String(nextLoanType === 'line_of_credit' ? 0 : (selectedDebt.amortYears || 30)),
                        interestOnlyMonths: String(
                          nextLoanType === 'interest_only' || nextLoanType === 'amortizing_balloon'
                            ? (selectedDebt.interestOnlyMonths || 12)
                            : selectedDebt.interestOnlyMonths
                        )
                      };
                      setDraftDebt(nextDraft);
                      setDraftDebtOwnerId(selectedDebt.id);
                    }}
                    disabled={!debtDetailsEditable}
                    className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500 disabled:cursor-default disabled:border-slate-800 disabled:bg-slate-950/60 disabled:text-slate-300 disabled:opacity-100"
                  >
                    {LOAN_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Lender</label>
                  <input
                    value={selectedDebtDraft?.lender || ''}
                    onChange={(e: any) => updateDraftField('lender', e.target.value)}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium outline-none ${debtDetailsEditable ? 'border-slate-700 bg-slate-950 text-white focus:border-emerald-500' : 'cursor-default border-slate-800 bg-slate-950/60 text-slate-300'}`}
                    readOnly={!debtDetailsEditable}
                    tabIndex={debtDetailsEditable ? 0 : -1}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Ownership Entity / LLC</label>
                  <select
                    value={isCustomDebtEntity ? '__custom__' : (selectedDebtDraft?.ownershipEntity || '')}
                    onChange={(e: any) => {
                      const nextValue = e.target.value;
                      updateDraftField('ownershipEntity', nextValue === '__custom__' ? '' : nextValue);
                    }}
                    disabled={!debtDetailsEditable}
                    className="w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-white outline-none focus:border-emerald-500 disabled:cursor-default disabled:border-slate-800 disabled:bg-slate-950/60 disabled:text-slate-300 disabled:opacity-100"
                  >
                    <option value="">No ownership entity selected</option>
                    {ownershipEntityOptions.map((entity: string) => (
                      <option key={entity} value={entity}>{entity}</option>
                    ))}
                    <option value="__custom__">Add new entity...</option>
                  </select>
                  {(isCustomDebtEntity || !selectedDebtDraft?.ownershipEntity) && (
                    <input
                      value={selectedDebtDraft?.ownershipEntity || ''}
                      onChange={(e: any) => updateDraftField('ownershipEntity', e.target.value)}
                      className={`w-full rounded-2xl border px-4 py-3 text-sm font-medium outline-none ${debtDetailsEditable ? 'border-slate-700 bg-slate-950 text-white focus:border-emerald-500' : 'cursor-default border-slate-800 bg-slate-950/60 text-slate-300'}`}
                      readOnly={!debtDetailsEditable}
                      tabIndex={debtDetailsEditable ? 0 : -1}
                      placeholder="Myli-Davidson LLC"
                    />
                  )}
                </div>
                <label className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-4">
                  <span className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Shared / Portfolio Debt</span>
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-slate-300">Treat this as a portfolio loan that can be allocated analytically across properties in the same entity.</p>
                    <input
                      type="checkbox"
                      checked={Boolean(selectedDebtDraft?.isSharedPortfolioDebt)}
                      disabled={!debtDetailsEditable}
                      onChange={(e: any) => updateDraftField('isSharedPortfolioDebt', e.target.checked)}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500/20"
                    />
                  </div>
                </label>
              </div>

              <textarea
                value={selectedDebtDraft?.notes || ''}
                onChange={(e: any) => updateDraftField('notes', e.target.value)}
                className={`mt-4 min-h-[100px] w-full rounded-2xl border px-4 py-3 text-sm outline-none ${debtDetailsEditable ? 'border-slate-700 bg-slate-950 text-slate-200 focus:border-emerald-500' : 'cursor-default border-slate-800 bg-slate-950/60 text-slate-400'}`}
                placeholder="Notes about servicing, balloon risk, covenants, or lender terms"
                readOnly={!debtDetailsEditable}
                tabIndex={debtDetailsEditable ? 0 : -1}
              />

              <div className="mt-4 overflow-hidden rounded-[2rem] border border-slate-700/70 bg-slate-950/40">
                <div className="border-b border-slate-700/60 px-5 py-4">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Live Loan Structure</p>
                      <p className="mt-2 text-sm text-slate-400">The balance now advances by payment date and extra principal events instead of staying static.</p>
                    </div>
                    <div
                      className={`rounded-2xl border px-4 py-3 text-sm ${
                        selectedDebt.assumeScheduledPaymentsMade
                          ? 'border-[color:var(--line-strong)] bg-[color:var(--surface-soft)] text-[color:var(--text-primary)]'
                          : 'border-[color:var(--line)] bg-[color:var(--surface-soft)] text-[color:var(--text-secondary)]'
                      }`}
                    >
                      {selectedDebt.assumeScheduledPaymentsMade ? 'Scheduled payments advance automatically by date.' : 'Scheduled payments are not auto-advanced.'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 p-5">
                  {fieldGroups.map(renderField)}
                  <div className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Scheduled Monthly Payment</p>
                    <p className="mt-2 text-xl font-black text-white">{formatCurrency0(displaySelectedDebtSnapshot.scheduledMonthlyPayment)}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Interest Portion</p>
                        <p className="mt-1 font-bold text-slate-200">{formatCurrency0(displaySelectedDebtSnapshot.currentInterestPortion)}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Principal Portion</p>
                        <p className="mt-1 font-bold text-slate-200">{formatCurrency0(displaySelectedDebtSnapshot.currentPrincipalPortion)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400"><Wallet size={12} className="inline mr-2 text-emerald-400" />Original Balance</p>
                <p className="mt-3 text-2xl font-black text-white">{formatCurrency0(selectedDebt.originalBalance)}</p>
              </div>
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400"><Building2 size={12} className="inline mr-2 text-cyan-400" />Live Current Balance</p>
                <p className="mt-3 text-2xl font-black text-white">{formatCurrency0(displaySelectedDebtSnapshot.currentBalance)}</p>
              </div>
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400"><Percent size={12} className="inline mr-2 text-amber-400" />Scheduled Payment</p>
                <p className="mt-3 text-2xl font-black text-white">{formatCurrency0(displaySelectedDebtSnapshot.scheduledMonthlyPayment)}</p>
              </div>
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400"><CalendarClock size={12} className="inline mr-2 text-emerald-400" />Next Payment Date</p>
                <p className="mt-3 text-2xl font-black text-white">{displaySelectedDebtSnapshot.nextPaymentDate || 'Paid Off'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Current Interest Portion</p>
                <p className="mt-3 text-2xl font-black text-rose-300">{formatCurrency0(displaySelectedDebtSnapshot.currentInterestPortion)}</p>
              </div>
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Current Principal Portion</p>
                <p className="mt-3 text-2xl font-black text-cyan-300">{formatCurrency0(displaySelectedDebtSnapshot.currentPrincipalPortion)}</p>
              </div>
              <div className="rounded-3xl border border-slate-700/60 bg-slate-900 p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400"><Clock3 size={12} className="inline mr-2 text-amber-400" />Remaining Term / Payoff</p>
                <p className="mt-3 text-2xl font-black text-white">{displaySelectedDebtSnapshot.remainingTermMonths} mo</p>
                <p className="mt-1 text-xs text-slate-500">Estimated payoff {displaySelectedDebtSnapshot.estimatedPayoffDate || 'TBD'}</p>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
              <div className="flex items-center justify-between gap-4 mb-5">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Extra Principal Payments</p>
                  <p className="mt-2 text-sm text-slate-500">Extra payments reduce the balance immediately and future amortization recalculates from the lower balance.</p>
                </div>
                <button
                  onClick={() => setShowExtraPaymentForm((current: boolean) => !current)}
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100"
                >
                  <Plus size={14} className="inline mr-2" /> Add Extra Payment
                </button>
              </div>

              {showExtraPaymentForm && (
                <div className="mb-4 rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Payment Date</label>
                      <input
                        type="date"
                        value={draftExtraPayment.date}
                        onChange={(e: any) => setDraftExtraPayment((current: { date: string; amount: number; notes: string }) => ({ ...current, date: e.target.value }))}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Amount</label>
                      <input
                        type="number"
                        min={0}
                        value={draftExtraPayment.amount}
                        onChange={(e: any) => setDraftExtraPayment((current: { date: string; amount: number; notes: string }) => ({ ...current, amount: Number(e.target.value) || 0 }))}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Notes</label>
                      <input
                        type="text"
                        value={draftExtraPayment.notes}
                        onChange={(e: any) => setDraftExtraPayment((current: { date: string; amount: number; notes: string }) => ({ ...current, notes: e.target.value }))}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-emerald-500"
                        placeholder="Optional note"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => {
                        if (!selectedDebt || draftExtraPayment.amount <= 0) return;
                        onSaveDebtEvent({
                          ...createDebtEvent(selectedDebt.id),
                          date: draftExtraPayment.date || todayIso(),
                          amount: draftExtraPayment.amount,
                          notes: draftExtraPayment.notes
                        });
                        setDraftExtraPayment({ date: todayIso(), amount: 0, notes: '' });
                        setShowExtraPaymentForm(false);
                      }}
                      className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100"
                    >
                      Save Extra Payment
                    </button>
                    <button
                      onClick={() => setShowExtraPaymentForm(false)}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {selectedDebtEvents.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center">
                    <p className="text-sm font-black text-slate-900">No extra principal payments logged.</p>
                    <p className="mt-2 text-[11px] text-slate-500">Add one to reduce the live balance and recalculate future amortization.</p>
                  </div>
                ) : selectedDebtEvents.map((event: DebtEvent) => (
                  <div key={event.id} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                    <div>
                      <p className="text-sm font-black text-white">{event.date}</p>
                      <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{event.type.replace(/_/g, ' ')}</p>
                      {event.notes ? <p className="mt-2 text-xs text-slate-400">{event.notes}</p> : null}
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-lg font-black text-cyan-300">{formatCurrency0(event.amount)}</p>
                      <button onClick={() => onDeleteDebtEvent(event.id)} className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-600 shadow-sm transition-colors hover:bg-rose-100">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
              <div className="flex items-center justify-between gap-4 mb-5">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Linked Properties</p>
                  <p className="mt-2 text-sm text-slate-500">One live loan can feed multiple properties. Allocations update from the current amortized balance and payment.</p>
                  <p className="mt-2 text-xs text-slate-500">This allocation is for property-level performance analysis only. The original loans remain unchanged for portfolio and tax reporting.</p>
                </div>
                <div className="flex items-center gap-3">
                  {isEditingLinks ? (
                    <>
                      <button
                        onClick={saveLinkDrafts}
                        className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100"
                      >
                        Save Links
                      </button>
                      <button
                        onClick={cancelEditingLinks}
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={beginEditingLinks}
                      disabled={isSelectedDebtPending}
                      className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                    >
                      Edit Links
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (isEditingLinks) {
                        const nextDrafts = Object.fromEntries(
                          resolvedLinksForSelectedDebt.map((entry: any) => {
                            const baseLink = (linkDrafts[entry.link.id] || entry.link) as DebtLinkDraft;
                            return [entry.link.id, {
                              ...baseLink,
                              allocationMethod: 'acquisition_price',
                              allocationPercent: entry.allocationPercent
                            }];
                          })
                        ) as Record<string, DebtLinkDraft>;
                        setLinkDrafts(nextDrafts);
                        return;
                      }
                      onAutoAllocateDebtByAcquisitionPrice(selectedDebt.id);
                    }}
                    disabled={isSelectedDebtPending}
                    className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-cyan-700 shadow-sm transition-colors hover:bg-cyan-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                  >
                    Auto Fill By Acquisition Price
                  </button>
                </div>
              </div>

              {Boolean(selectedDebtDraft?.isSharedPortfolioDebt) && (
                <div className="mb-5 grid grid-cols-1 gap-3 xl:grid-cols-[1.1fr,1fr,1fr]">
                  <div className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
                    <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Allocation Method</label>
                    <select
                      value={sharedDebtAllocationMethod}
                      onChange={(e: any) => applyAllocationMethodToAllLinks(e.target.value as PropertyDebtLink['allocationMethod'])}
                      disabled={!isEditingLinks}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:opacity-100"
                    >
                      <option value="manual">Manual</option>
                      <option value="acquisition_price">Acquisition Price</option>
                      <option value="equal_split">Equal Split</option>
                      <option value="current_value">Current Value</option>
                      <option value="normalize_ltv">Normalize by Portfolio LTV</option>
                    </select>
                    <p className="mt-2 text-xs text-slate-500">
                      {sharedDebtAllocationMethod === 'normalize_ltv'
                        ? 'Allocates shared portfolio debt across properties so each property has the same implied LTV.'
                        : 'Choose how this shared debt should be spread across eligible properties.'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
                    <label className="mb-2 block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Value Basis</label>
                    <select
                      value={sharedDebtValueBasis || 'purchase_price'}
                      onChange={(e: any) => applyValueBasisToAllLinks(e.target.value as PropertyDebtLink['valueBasis'])}
                      disabled={!isEditingLinks}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:opacity-100"
                    >
                      <option value="purchase_price">Purchase price</option>
                      <option value="current_value">Current estimated value</option>
                      <option value="manual">Manual value override</option>
                    </select>
                    <p className="mt-2 text-xs text-slate-500">Default value source used for the normalized-LTV allocation math.</p>
                  </div>
                  <div className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Calculated Portfolio LTV</p>
                    <p className="mt-2 text-lg font-black text-white">
                      {normalizedLtvState.impliedLtv == null ? 'N/A' : formatPercent2(normalizedLtvState.impliedLtv * 100)}
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      {selectedDebtEntity ? `Eligible properties are filtered to ${selectedDebtEntity}.` : 'Set an ownership entity to scope shared debt to one LLC.'}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-[1.2fr,auto,1fr,1fr] gap-3">
                <div>
                  <select
                    value={selectedPropertyToLink}
                    onChange={(e: any) => {
                      propertyPickerTouchedRef.current = true;
                      setSelectedPropertyToLink(e.target.value);
                    }}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:border-emerald-500"
                  >
                    <option value="">{selectedDebtDraft?.isSharedPortfolioDebt && selectedDebtEntity ? `Link ${selectedDebtEntity} property...` : 'Link property...'}</option>
                    {availablePropertiesToLink.map((property: Property) => (
                      <option key={property.id} value={property.id}>{property.name}</option>
                    ))}
                  </select>
                  {selectedDebtDraft?.isSharedPortfolioDebt && selectedDebtEntity ? (
                    <p className="mt-2 text-xs text-slate-500">
                      {eligibleEntityProperties.length} eligible in {selectedDebtEntity}; {linkedEligiblePropertyCount} already linked.
                    </p>
                  ) : null}
                </div>
                <button
                  onClick={async () => {
                    if (!selectedPropertyToLink) return;
                    if (!isSelectedDebtSynced) return;
                    await onUpsertDebtLink({
                      id: crypto.randomUUID(),
                      debtId: selectedDebtSyncId || selectedDebt.id,
                      propertyId: selectedPropertyToLinkSyncId || selectedPropertyToLink,
                      allocationPercent: 0,
                      allocationMethod: selectedDebtDraft?.isSharedPortfolioDebt ? sharedDebtAllocationMethod : 'acquisition_price',
                      valueBasis: sharedDebtValueBasis || 'purchase_price',
                      manualValueOverride: null
                    });
                    setSelectedPropertyToLink('');
                  }}
                  disabled={!selectedPropertyIsSynced || !isSelectedDebtSynced}
                  className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                >
                  Add Link
                </button>
                <div className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Allocation Total</p>
                  <p className={`mt-2 text-lg font-black ${allocationTotal > 100.01 ? 'text-rose-300' : Math.abs(allocationTotal - 100) < 0.5 ? 'text-emerald-300' : 'text-amber-300'}`}>{formatPercent2(allocationTotal)}</p>
                </div>
                <div className="rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Linked Count</p>
                  <p className="mt-2 text-lg font-black text-white">{resolvedLinksForSelectedDebt.length}</p>
                </div>
              </div>

              <div className={`mt-4 rounded-2xl border px-4 py-3 ${validationState.className}`}>
                <p className="text-[10px] font-black uppercase tracking-[0.2em]">{validationState.label}</p>
                <p className="mt-2 text-sm">{validationState.detail}</p>
              </div>

              <div className="mt-4 space-y-3">
                {resolvedLinksForSelectedDebt.map((entry: any) => {
                  const { link, property, allocationPercent, allocatedCurrentBalance, allocatedMonthlyPayment } = entry;
                  if (!property) return null;
                  const propertyInputs = property.currentYearInputs || property.inputs;
                  return (
                    <div key={link.id} className="grid grid-cols-1 xl:grid-cols-[1.4fr,1fr,1fr,1.2fr,auto] gap-3 rounded-2xl border border-slate-700/70 bg-slate-950/60 p-4">
                      <div>
                        <p className="text-sm font-black text-white">{property.name}</p>
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">{formatCurrency0(propertyInputs.purchasePrice)} acquisition price</p>
                      </div>
                      <div>
                        <label className="mb-2 block text-[9px] font-black uppercase tracking-widest text-slate-400">Method</label>
                        <select
                          value={(linkDrafts[link.id] || link).allocationMethod}
                          onChange={(e: any) => {
                            if (!isEditingLinks) return;
                            updateLinkDraft(link.id, {
                              allocationMethod: e.target.value as PropertyDebtLink['allocationMethod']
                            });
                          }}
                          disabled={!isEditingLinks}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:opacity-100"
                        >
                          <option value="manual">Manual</option>
                          <option value="acquisition_price">Acquisition Price</option>
                          <option value="equal_split">Equal Split</option>
                          <option value="current_value">Current Value</option>
                          <option value="normalize_ltv">Normalize by Portfolio LTV</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-[9px] font-black uppercase tracking-widest text-slate-400">Allocation %</label>
                        <input
                          type="number"
                          value={(linkDrafts[link.id] || link).allocationMethod === 'manual'
                            ? (linkDrafts[link.id] || link).allocationPercent
                            : allocationPercent}
                          onChange={(e: any) => {
                            if (!isEditingLinks) return;
                            updateLinkDraft(link.id, {
                              allocationPercent: Number(e.target.value) || 0,
                              allocationMethod: 'manual'
                            });
                          }}
                          readOnly={!isEditingLinks}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500 read-only:cursor-not-allowed read-only:border-slate-200 read-only:bg-slate-50 read-only:text-slate-500 read-only:opacity-100"
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-[9px] font-black uppercase tracking-widest text-slate-400">Value Basis</label>
                        <select
                          value={(linkDrafts[link.id] || link).valueBasis || 'purchase_price'}
                          onChange={(e: any) => {
                            if (!isEditingLinks) return;
                            updateLinkDraft(link.id, {
                              valueBasis: e.target.value as PropertyDebtLink['valueBasis']
                            });
                          }}
                          disabled={!isEditingLinks}
                          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:opacity-100"
                        >
                          <option value="purchase_price">Purchase Price</option>
                          <option value="current_value">Current Value</option>
                          <option value="manual">Manual Override</option>
                        </select>
                        {((linkDrafts[link.id] || link).valueBasis || 'purchase_price') === 'manual' ? (
                          <input
                            type="number"
                            value={(linkDrafts[link.id] || link).manualValueOverride ?? 0}
                            onChange={(e: any) => {
                              if (!isEditingLinks) return;
                              updateLinkDraft(link.id, {
                                manualValueOverride: Number(e.target.value) || 0
                              });
                            }}
                            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-emerald-500"
                            placeholder="Manual value"
                          />
                        ) : null}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Allocated Bal</p>
                          <p className="mt-2 text-sm font-black text-white">{formatCurrency0(allocatedCurrentBalance)}</p>
                        </div>
                        <div className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2">
                          <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Pmt</p>
                          <p className="mt-2 text-sm font-black text-white">{formatCurrency0(allocatedMonthlyPayment)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveDebtLink(link.id)}
                        disabled={isEditingLinks}
                        className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-600 shadow-sm transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-700/60 bg-slate-900 p-6">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Amortization Timeline</p>
              <p className="mt-2 text-sm text-slate-500">Scheduled payment rows and extra principal events live together here so the balance timeline stays transparent.</p>
              {showTimelineTable ? (
                <div className="mt-5 overflow-x-auto">
                  <table className="w-full min-w-[980px] text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-400 font-black uppercase text-[9px] tracking-[0.15em]">
                        <th className="pb-4">Date</th>
                        <th className="pb-4">Type</th>
                        <th className="pb-4 text-right">Beg Balance</th>
                        <th className="pb-4 text-right">Scheduled Pmt</th>
                        <th className="pb-4 text-right">Interest</th>
                        <th className="pb-4 text-right">Principal</th>
                        <th className="pb-4 text-right">Extra Principal</th>
                        <th className="pb-4 text-right">End Balance</th>
                        <th className="pb-4 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {displaySelectedDebtSnapshot.scheduleRows.map((row: DebtScheduleRow) => (
                        <tr key={row.id} className="hover:bg-slate-700/20 transition-colors">
                          <td className="py-4 font-mono text-slate-300">{row.date}</td>
                          <td className="py-4 text-slate-200">{row.rowType === 'scheduled_payment' ? 'Scheduled Payment' : 'Extra Principal'}</td>
                          <td className="py-4 text-right font-mono text-slate-400">{formatCurrency0(row.beginningBalance)}</td>
                          <td className="py-4 text-right font-mono text-slate-300">{row.scheduledPayment > 0 ? formatCurrency0(row.scheduledPayment) : '—'}</td>
                          <td className="py-4 text-right font-mono text-rose-300">{row.interestPortion > 0 ? formatCurrency0(row.interestPortion) : '—'}</td>
                          <td className="py-4 text-right font-mono text-cyan-300">{row.principalPortion > 0 ? formatCurrency0(row.principalPortion) : '—'}</td>
                          <td className="py-4 text-right font-mono text-cyan-200">{row.extraPrincipal > 0 ? formatCurrency0(row.extraPrincipal) : '—'}</td>
                          <td className="py-4 text-right font-mono text-white">{formatCurrency0(row.endingBalance)}</td>
                          <td className="py-4 text-right">
                            <span className={`rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-widest ${row.status === 'past' ? 'border border-slate-600 bg-slate-800 text-slate-300' : 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300'}`}>
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-slate-700/70 bg-slate-950/50 p-5 text-sm text-slate-400">
                  Preparing amortization timeline...
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebtView;
